import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Globe2, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HKBadge } from "@/components/site/HKBadge";
import { CTASection } from "@/components/site/CTASection";
import { REGIONS } from "@/lib/content";
import { Seo } from "@/components/site/Seo";

export default function LocalizationPage() {
  const { t } = useTranslation();
  const checklistItems = t("common.marketEntryItems", { returnObjects: true }) || [];
  return (
    <div data-testid="localization-page">
      <Seo
        title={t("seo.localization.metaTitle")}
        description={t("seo.localization.metaDescription")}
        keywords={t("seo.localization.keywords")}
        path="/localization"
        breadcrumbs={[
          { name: t("seo.breadcrumbHome"), url: "/" },
          { name: t("seo.localization.breadcrumbLabel"), url: "/localization" },
        ]}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dc-dot-grid opacity-50" aria-hidden />
        <div className="relative mx-auto w-full max-w-6xl px-4 pb-10 pt-14 sm:px-6 sm:pb-14 sm:pt-20 lg:px-8">
          <HKBadge />
          <h1 className="mt-5 max-w-4xl text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-[58px] lg:leading-[1.05]">
            {t("pages.localizationTitle")}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">{t("pages.localizationSubtitle")}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="localization-hero-cta">
              <Button className="h-11 rounded-lg bg-blue-600 px-5 text-white hover:bg-blue-700">
                {t("cta.planMarketEntry")} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/industries">
              <Button variant="outline" className="h-11 rounded-lg border-slate-300 bg-white px-5 text-slate-900 hover:bg-slate-50">
                {t("cta.seeVerticals")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 pb-16 sm:px-6 sm:pb-20 lg:px-8">
        <Tabs defaultValue={REGIONS[0].key}>
          <TabsList data-testid="localization-region-tabs" className="flex h-auto w-full flex-wrap justify-start gap-2 rounded-xl bg-slate-50 p-2 sm:w-auto sm:inline-flex">
            {REGIONS.map((r) => (
              <TabsTrigger key={r.key} value={r.key} data-testid={`region-tab-${r.key}`} className="rounded-lg px-3 py-1.5 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm">
                {t(`content.${r.tk}.label`)}
              </TabsTrigger>
            ))}
          </TabsList>
          {REGIONS.map((r) => {
            const markets = t(`content.${r.tk}.markets`, { returnObjects: true }) || [];
            const playbook = t(`content.${r.tk}.playbook`, { returnObjects: true }) || [];
            const label = t(`content.${r.tk}.label`);
            return (
              <TabsContent key={r.key} value={r.key} className="mt-6" data-testid={`region-detail-${r.key}`}>
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="grid gap-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 lg:grid-cols-3">
                  <div className="lg:col-span-2">
                    <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800">
                      <Globe2 className="h-4 w-4" /> {label}
                    </div>
                    <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{t(`content.${r.tk}.headline`)}</h2>
                    <p className="mt-3 text-base leading-relaxed text-slate-600">{t(`content.${r.tk}.summary`)}</p>
                    <div className="mt-6">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.priorityMarkets")}</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {markets.map((m) => (
                          <span key={m} className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">{m}</span>
                        ))}
                      </div>
                    </div>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2">
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.primaryChannels")}</div>
                        <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm text-slate-800">
                          {r.channels.map((c) => (
                            <li key={c} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> {c}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("common.localPlaybook")}</div>
                        <ul className="mt-3 space-y-2 text-sm text-slate-800">
                          {playbook.map((p) => (
                            <li key={p} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {p}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  <aside className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                    <h3 className="text-base font-bold text-slate-900">{t("common.marketEntryChecklist")}</h3>
                    <ul className="mt-3 space-y-2 text-sm text-slate-700">
                      {checklistItems.map((c) => (
                        <li key={c} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {c}</li>
                      ))}
                    </ul>
                    <Link to="/contact" className="mt-5 inline-flex items-center text-sm font-semibold text-blue-700">
                      {t("cta.discussRegion", { region: label })} <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </aside>
                </motion.div>
              </TabsContent>
            );
          })}
        </Tabs>
      </section>

      <CTASection title={t("ctaSection.localizationTitle")} primaryLabel={t("cta.planMarketEntry")} />
    </div>
  );
}
